import {
  type Possibilities,
  useVariantPossibilities,
} from "~/sdk/useVariantPossiblities";
import type {
  Product,
  ProductLeaf,
  PropertyValue,
} from "@decocms/apps-commerce/types";
import { useEffect, useRef, useState } from "react";
import { Fragment } from "react";
import { createPortal } from "react-dom";
import AddToCartButtonVTEX from "~/components/product/AddToCartButton/vtex";
import type { IParams } from "./types";
import { getColorByName as getColorByNameUtil } from "~/utils/colorConfig";
import { useUI } from "~/sdk/useUI";
import { sanitizeStoreUrl } from "~/utils/constants";

export interface Props {
  product: Product;
  listPrice: number;
  price: number;
  seller: string | number;
  setCurrentSku: (sku: string | number) => void;
  hideButton?: boolean;
  setProductAvailable?: (isAvailable: boolean) => void;
  setProductParams?: (params: IParams) => void;
}

type VariantPossibilityMap = Possibilities;

const getPropertyValue = (
  variant: ProductLeaf,
  propertyName: string,
) =>
  variant.additionalProperty?.find((prop: PropertyValue) =>
    prop.name === propertyName
  )?.value;

const hasInventory = (variant: ProductLeaf) =>
  (variant.offers?.offers?.[0]?.inventoryLevel?.value ?? 0) > 0;

function VariantSelectorShelf({
  product,
  listPrice,
  price,
  seller,
  setCurrentSku,
  hideButton,
  setProductParams,
  setProductAvailable,
}: Props) {
  const { isVariantOf } = product;
  const hasVariant = isVariantOf?.hasVariant ?? [];
  const possibilities = useVariantPossibilities(hasVariant, product);
  const [params, setParams] = useState<IParams>({
    size: "",
    color: "",
  });
  const [sku, setSku] = useState(product.sku);
  const [available, setAvailable] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { displayCart } = useUI();

  // Fecha o modal quando o carrinho é aberto (produto foi adicionado)
  useEffect(() => {
    if (displayCart.value && isModalOpen) {
      setIsModalOpen(false);
    }
  }, [displayCart.value]);

  // Trava o scroll do body enquanto o modal mobile está aberto — sem isso,
  // o usuário rola a página por trás do bottom-sheet fixo, dando a sensação
  // de vitrine desconfigurada (conteúdo se move, modal fica parado).
  useEffect(() => {
    if (!isModalOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isModalOpen]);

  const handleSelectChange = () => {
    if (!hasVariant) return;

    const variation = hasVariant.filter((variant) => {
      const sizeProperty = getPropertyValue(variant, "Tamanho");
      const colorProperty = getPropertyValue(variant, "Cor");

      return (
        (params.size ? sizeProperty === params.size : true) &&
        (params.color ? colorProperty === params.color : true)
      );
    });

    if (!params.size && !params.color) return;

    if (variation.length > 0) {
      setSku(variation[0].sku);
      setCurrentSku(variation[0].sku);
    }

    const selectedVariant = variation.find(
      (variant) => hasInventory(variant),
    );

    setAvailable(!!selectedVariant);
    if (setProductAvailable) {
      setProductAvailable(!!selectedVariant);
    }
  };

  useEffect(() => {
    const variants = product.isVariantOf?.hasVariant;
    const hasAvailableVariant = variants?.some(
      (variant) => hasInventory(variant),
    );
    setAvailable(!!hasAvailableVariant);
    if (setProductAvailable) {
      setProductAvailable(!!hasAvailableVariant);
    }
  }, [product]);

  useEffect(() => {
    handleSelectChange();

    if (setProductParams) {
      setProductParams(params);
    }
  }, [params]);

  // Pré-seleciona tamanho/cor da primeira variação em estoque, pra virar
  // compra em um clique na vitrine. Roda só uma vez (didSeedRef) — troca
  // manual do usuário mexe em `params`, não em `hasVariant`, então nunca
  // dispara de novo e nunca sobrescreve a escolha do cliente.
  const didSeedRef = useRef(false);
  useEffect(() => {
    if (didSeedRef.current) return;
    const firstInStock = hasVariant.find((variant) => hasInventory(variant));
    if (!firstInStock) return;
    const size = getPropertyValue(firstInStock, "Tamanho") ?? "";
    const color = getPropertyValue(firstInStock, "Cor") ?? "";
    if (!size && !color) return;
    didSeedRef.current = true;
    setParams({ size, color });
  }, [hasVariant]);

  const filteredPossibilities = Object.keys(possibilities)
    .filter((name) => name !== "Estimated Date Arrival")
    .reduce((obj, key) => {
      const value = possibilities[key];
      if (value) {
        obj[key] = value;
      }
      return obj;
    }, {} as VariantPossibilityMap);

  // Função para obter a cor baseada no nome (usa as cores padrão do colorConfig.ts)
  const getColorByName = (colorName: string): string => {
    return getColorByNameUtil(colorName);
  };

  // Função para verificar se uma cor específica está disponível
  const isColorAvailable = (colorValue: string) => {
    return hasVariant.some((variant) => {
      const colorProperty = getPropertyValue(variant, "Cor");
      return colorProperty === colorValue && hasInventory(variant);
    });
  };

  // Função para verificar se um tamanho específico está disponível
  const isSizeAvailable = (sizeValue: string) => {
    return hasVariant.some((variant) => {
      const sizeProperty = getPropertyValue(variant, "Tamanho");
      return sizeProperty === sizeValue && hasInventory(variant);
    });
  };

  return (
    <Fragment>
      <div className="flex flex-col gap-3 w-full md:items-center md:justify-center md:pb-2 min-[1025px]:h-[58px] min-[1025px]:absolute min-[1025px]:bottom-[17px] absolute bottom-0 left-0">
        {/* Botão de Compra - Mobile */}
        <div className="flex justify-start ml-2 min-[1025px]:hidden w-[95%] mb-2">
          <button
            onClick={() => setIsModalOpen(true)}
            aria-label="Adicionar à sacola"
            className="w-8 h-8 cursor-pointer self-center bg-[#FFFFFF] text-center py-1 transition-colors shadow-[0_0_8px_0_#1A1A1A14]"
          >
            <span className="flex items-center justify-center w-full font-medium text-[11px] text-white uppercase tracking-wide">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect x="11" y="6" width="2" height="12" fill="#1A1A1A" />
                <rect
                  x="18"
                  y="11"
                  width="2"
                  height="12"
                  transform="rotate(90 18 11)"
                  fill="#1A1A1A"
                />
              </svg>
            </span>
          </button>
        </div>

        {/* Container Desktop - Variações + Botão */}
        <div className="hidden min-[1025px]:group-hover:flex gap-2 w-full items-end justify-center bg-[#FFFFFFE0] backdrop-blur-[6px] p-3 variant-bg">
          {/* Container vertical: Cores em cima, Tamanhos embaixo */}
          <div className="flex flex-col items-center gap-2 max-w-[178px]">
            {/* Seletor de Cores */}
            {filteredPossibilities["Cor"] && (
              <div className="flex gap-1 overflow-x-auto max-w-full variant-scroll pb-1">
                {Object.entries(filteredPossibilities["Cor"])
                  .map(([colorValue, _link]) => {
                    const isSelected = params.color === colorValue;
                    const isAvailable = isColorAvailable(colorValue);
                    const colorHex = getColorByName(colorValue);

                    return (
                      <button
                        key={colorValue}
                        onClick={() =>
                          isAvailable &&
                          setParams((before) => ({
                            ...before,
                            color: colorValue,
                          }))}
                        className={`w-8 h-8 shrink-0 rounded-full border-[1px] transition-all relative bg-white ${
                          isSelected ? "border-[#1A1A1A]" : "border-[#E6E6E6]"
                        } ${
                          !isAvailable
                            ? "opacity-40 cursor-not-allowed"
                            : "cursor-pointer hover:border-[#1A1A1A]"
                        }`}
                        title={`${colorValue} ${
                          !isAvailable ? "(Indisponível)" : ""
                        }`}
                        disabled={!isAvailable}
                      >
                        {/* Círculo colorido interno */}
                        <div
                          className="w-[22px] h-[22px] rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                          style={{ backgroundColor: colorHex }}
                        >
                          {colorValue === "BRANCO" && (
                            <div className="absolute inset-0 rounded-full border border-[#E6E6E6]">
                            </div>
                          )}
                        </div>
                        {!isAvailable && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-6 h-0.5 bg-[#8b8b8b] rotate-45">
                            </div>
                          </div>
                        )}
                      </button>
                    );
                  })}
              </div>
            )}

            {/* Seletor de Tamanho */}
            {filteredPossibilities["Tamanho"] && (
              <div className="flex gap-1 overflow-x-auto max-w-full variant-scroll pb-1">
                {Object.entries(filteredPossibilities["Tamanho"])
                  .sort(([valueA], [valueB]) => {
                    const isNumberA = !isNaN(Number(valueA));
                    const isNumberB = !isNaN(Number(valueB));

                    // only sorting if the values are numbers because string sizes (P, M, G) should not be sorted
                    if (isNumberA && isNumberB) {
                      return Number(valueA) - Number(valueB);
                    } else {
                      return 0;
                    }
                  })
                  .map(([sizeValue, _link]) => {
                    const isSelected = params.size === sizeValue;
                    const isAvailable = isSizeAvailable(sizeValue);

                    return (
                      <button
                        key={sizeValue}
                        onClick={() =>
                          isAvailable &&
                          setParams((before) => ({
                            ...before,
                            size: sizeValue,
                          }))}
                        className={`w-[28px] h-[28px] flex-shrink-0 rounded-full border-[1px] flex items-center justify-center text-xs font-normal transition-all relative ${
                          isSelected
                            ? "border-[#1A1A1A] bg-[#1A1A1A] text-white"
                            : `border-[#E6E6E6] bg-white text-[#1A1A1A] ${
                              isAvailable ? "hover:border-[#1A1A1A]" : ""
                            }`
                        } ${
                          !isAvailable
                            ? "opacity-40 cursor-not-allowed"
                            : "cursor-pointer"
                        }`}
                        title={`${sizeValue} ${
                          !isAvailable ? "(Indisponível)" : ""
                        }`}
                        disabled={!isAvailable}
                      >
                        {sizeValue}
                        {!isAvailable && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-full h-[1px] bg-[#8b8b8b] rotate-45">
                            </div>
                          </div>
                        )}
                      </button>
                    );
                  })}
              </div>
            )}
          </div>

          {/* Botão de Sacola - Ícone ao lado */}
          {available
            ? (
              params.size != "" && params.color != ""
                ? (
                  <div
                    id="buyButton"
                    className={`${hideButton ? "hidden" : "flex"}`}
                  >
                    <AddToCartButtonVTEX
                      product={product}
                      listPrice={listPrice!}
                      skuId={sku}
                      price={price}
                      seller={String(seller)}
                      id="fixedBuyButton"
                      className="w-9 h-9 bg-[#1A1A1A] hover:bg-[#C18A58] transition-colors flex items-center justify-center cursor-pointer group/bag"
                    >
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                        >
                        </path>
                      </svg>
                    </AddToCartButtonVTEX>
                  </div>
                )
                : (
                  <button
                    onClick={() => alert("Selecione cor e tamanho!")}
                    className={`w-9 h-9 bg-[#1A1A1A] hover:bg-[#C18A58] transition-colors flex items-center justify-center cursor-pointer ${
                      hideButton ? "hidden" : "flex"
                    }`}
                    title="Adicionar à sacola"
                  >
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                      >
                      </path>
                    </svg>
                  </button>
                )
            )
            : (
              <button
                disabled
                className="w-9 h-9 bg-gray-400 transition-colors flex items-center justify-center cursor-not-allowed"
                title="Indisponível"
              >
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                  >
                  </path>
                </svg>
              </button>
            )}
        </div>
      </div>
      {/* Modal Mobile — renderizado via portal no document.body. O card da
          vitrine vive dentro de containers com overflow-hidden/transform, que
          quebram o position: fixed: o overlay cobria só a imagem do produto e o
          bottom-sheet ia parar bem abaixo, cortado. */}
      {isModalOpen && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 z-50 min-[1025px]:hidden">
          <div
            className="absolute inset-0 bg-[#1A1A1A]/50"
            onClick={() => setIsModalOpen(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-white px-5 pt-12 pb-8 max-h-[85vh] overflow-y-auto">
            {/* Barra superior do modal */}
            <div className="flex justify-center items-center mb-6 relative">
              <div
                className="absolute -top-11 left-0 right-0 h-8 flex justify-center items-center cursor-pointer"
                onClick={() => setIsModalOpen(false)}
              >
                <div className="w-12 h-0.5 bg-[#434343] rounded-full"></div>
              </div>
              <h3 className="text-[13px] font-medium uppercase tracking-wide text-[#434343]">
                Adicione à Sacola
              </h3>
            </div>

            {/* Seletor de Cores no Modal */}
            {filteredPossibilities["Cor"] && (
              <div className="mb-6">
                <div className="flex justify-center gap-[3px]">
                  {Object.entries(filteredPossibilities["Cor"]).map(
                    ([colorValue, _link]) => {
                      const isSelected = params.color === colorValue;
                      const isAvailable = isColorAvailable(colorValue);
                      const colorHex = getColorByName(colorValue);

                      return (
                        <button
                          key={colorValue}
                          onClick={() =>
                            isAvailable &&
                            setParams((before) => ({
                              ...before,
                              color: colorValue,
                            }))}
                          className={`w-8 h-8 rounded-full border-[1px] transition-all relative bg-white ${
                            isSelected ? "border-[#1A1A1A]" : "border-gray-300"
                          } ${
                            !isAvailable
                              ? "opacity-40 cursor-not-allowed"
                              : "cursor-pointer hover:border-[#1A1A1A]"
                          }`}
                          title={`${colorValue} ${
                            !isAvailable ? "(Indisponível)" : ""
                          }`}
                          disabled={!isAvailable}
                        >
                          {/* Círculo colorido interno */}
                          <div
                            className="w-[22px] h-[22px] rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                            style={{ backgroundColor: colorHex }}
                          >
                            {colorValue === "BRANCO" && (
                              <div className="absolute inset-0 rounded-full border border-gray-200">
                              </div>
                            )}
                          </div>
                          {!isAvailable && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-8 h-0.5 bg-[#8b8b8b] rotate-45">
                              </div>
                            </div>
                          )}
                        </button>
                      );
                    },
                  )}
                </div>
              </div>
            )}

            {/* Seletor de Tamanho no Modal */}
            {filteredPossibilities["Tamanho"] && (
              <div className="mb-4">
                <div className="flex justify-center gap-[3px] mb-6">
                  {Object.entries(filteredPossibilities["Tamanho"])
                    .sort(([valueA], [valueB]) => {
                      const isNumberA = !isNaN(Number(valueA));
                      const isNumberB = !isNaN(Number(valueB));

                      if (isNumberA && isNumberB) {
                        return Number(valueA) - Number(valueB);
                      } else {
                        return 0;
                      }
                    })
                    .map(([sizeValue, _link]) => {
                      const isSelected = params.size === sizeValue;
                      const isAvailable = isSizeAvailable(sizeValue);

                      return (
                        <button
                          key={sizeValue}
                          onClick={() =>
                            isAvailable &&
                            setParams((before) => ({
                              ...before,
                              size: sizeValue,
                            }))}
                          className={`w-8 h-8 rounded-full border-[1px] flex items-center justify-center text-sm font-normal transition-all relative ${
                            isSelected
                              ? "border-[#1A1A1A] bg-[#1A1A1A] text-white"
                              : `border-[#E6E6E6] bg-white text-[#1A1A1A] ${
                                isAvailable ? "hover:border-[#1A1A1A]" : ""
                              }`
                          } ${
                            !isAvailable
                              ? "opacity-40 cursor-not-allowed"
                              : "cursor-pointer"
                          }`}
                          title={`${sizeValue} ${
                            !isAvailable ? "(Indisponível)" : ""
                          }`}
                          disabled={!isAvailable}
                        >
                          {sizeValue}
                          {!isAvailable && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-full h-[1px] bg-[#8b8b8b] rotate-45">
                              </div>
                            </div>
                          )}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Botão de Adicionar à Sacola no Modal */}
            <div className="flex flex-col gap-3 w-full">
              {available
                ? (
                  params.size !== "" && params.color !== ""
                    ? (
                      <AddToCartButtonVTEX
                        product={product}
                        listPrice={listPrice!}
                        skuId={sku}
                        price={price}
                        seller={String(seller)}
                        id="modalBuyButton"
                        className="w-full h-[38px] bg-[#1A1A1A] text-white font-medium text-sm uppercase tracking-wide hover:bg-[#C18A58] transition-colors flex items-center justify-center"
                      >
                        COMPRAR
                      </AddToCartButtonVTEX>
                    )
                    : (
                      <button
                        onClick={() => alert("Selecione cor e tamanho!")}
                        className="w-full h-[38px] bg-[#1A1A1A] text-white font-medium text-sm uppercase tracking-wide hover:bg-[#C18A58] transition-colors flex items-center justify-center"
                      >
                        COMPRAR
                      </button>
                    )
                )
                : (
                  <button
                    disabled
                    className="w-full h-[38px] bg-[#ccc] text-white font-medium text-sm uppercase tracking-wide cursor-not-allowed flex items-center justify-center"
                  >
                    INDISPONÍVEL
                  </button>
                )}

              {/* Link Ver Mais Detalhes */}
              <a
                href={sanitizeStoreUrl(product.url)}
                className="text-center text-[13px] text-[#757575] uppercase tracking-wide hover:text-[#1A1A1A] transition-colors"
                onClick={() => setIsModalOpen(false)}
              >
                Ver Mais Detalhes
              </a>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </Fragment>
  );
}

export default VariantSelectorShelf;
